import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Coins, X, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';

interface InsufficientFundsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onGoToMarket: () => void;
    requiredAmount: number;
    currentAmount: number;
    /** Overrides the default message when the action context warrants a specific prompt. */
    customMessage?: string;
}

export function InsufficientFundsModal({
    isOpen,
    onClose,
    onGoToMarket,
    requiredAmount,
    currentAmount,
    customMessage,
}: InsufficientFundsModalProps) {
    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-sm bg-[#E6D5B8] dark:bg-[#1a1a1a] rounded-3xl shadow-xl overflow-hidden border border-black/10 dark:border-white/10"
                        >
                            <div className="p-6 flex flex-col items-center text-center">
                                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                                    <Coins className="w-8 h-8 text-red-600 dark:text-red-400" />
                                </div>

                                <h3 className="text-2xl font-bold text-black dark:text-white mb-2">
                                    Out of Gold
                                </h3>

                                <p className="text-[#4a4a4a] dark:text-[#999999] mb-3">
                                    {customMessage ?? (
                                        <>
                                            You need{' '}
                                            <span className="font-bold text-amber-600 dark:text-amber-400">{requiredAmount} gold</span>
                                            {' '}to make this connection, but you only have {currentAmount}.
                                        </>
                                    )}
                                </p>
                                <p className="text-[#888888] dark:text-[#666666] text-xs mb-6">
                                    Earn gold through your daily login streak or by watching ads.
                                </p>

                                <div className="w-full space-y-3">
                                    <Button
                                        onClick={onClose}
                                        className="w-full h-12 rounded-xl bg-gray-200 dark:bg-gray-800 text-black dark:text-white font-bold shadow-lg button-shadow flex items-center justify-center gap-2"
                                    >
                                        Okay
                                    </Button>

                                    <button
                                        onClick={onClose}
                                        className="w-full py-3 text-[#666666] dark:text-[#999999] font-medium hover:text-black dark:hover:text-white transition-colors"
                                    >
                                        Maybe Later
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
